import React, { Fragment, useEffect, useState } from 'react';
import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import { tutorialConstants } from '../../../../utils/constants';
import { HelideckDropTestItem, HelideckNetDropSummary, HelideckVIItem, PDFCoverSheetProps, PDFImageProps, VTEquipmentAnomalyData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import HelideckCoverForm from '../../../forms/helideckCoverForm';
import HelideckDrawingSheetForm from '../../../forms/HelideckDrawingSheetForm';
import HelideckDropPhotoForm from '../../../forms/helideck-net-drop/helideckDropPhotoForm';
import HelideckDropSketchForm from '../../../forms/helideck-net-drop/helideckDropSketchForm';
import HelideckNetDropTestReport from '.';
import HelideckDropSummaryForm from '../../../forms/helideck-net-drop/helideckDropSummaryForm';
import HelideckDropTestItemsForm from '../../../forms/helideck-net-drop/helideckDropTestItemsForm';
import HelideckVIItemsForm from '../../../forms/helideck-net-drop/helideckVIItemsForm';
import Modal from '../../../themed/modal';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateHelideckNetDropReport: React.FC = () => {
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [coverSheetData, setCoverSheetData] = useState<PDFCoverSheetProps>();
	const [summaryData, setSummaryData] = useState<HelideckNetDropSummary>();
	const [drawingSheetData, setDrawingSheetData] = useState<PDFImageProps[]>([]);
	const [sketchData, setSketchData] = useState<PDFImageProps[]>([]);
	const [viItems, setVIItems] = useState<HelideckVIItem[]>([]);
	const [dtItems, setDTItems] = useState<HelideckDropTestItem[]>([]);
	const [photographicData, setPhotographicData] = useState<any>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);

	const getReportDetails = () => {
		return {
			anomalyData,
			coverSheetData,
			summarySheetData: summaryData,
			drawingSheetPhotos: drawingSheetData,
			sketchData,
			viTableData: viItems,
			dtTableData: dtItems,
			photographicRecords: photographicData,
			projectDetails,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setCoverSheetData(cachedReport.coverSheetData);
			setSummaryData(cachedReport.summarySheetData);
			setPhotographicData(cachedReport.photographicRecords);
			setSketchData(cachedReport.sketchData);
			setVIItems(cachedReport.viTableData);
			setDTItems(cachedReport.dtTableData);
			setDrawingSheetData(cachedReport.drawingSheetPhotos);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<HelideckNetDropTestReport {...reportDetails} />
			</Modal>

			<Fragment>
				<Zippy headerTitle="Cover Sheet" isValid={!!coverSheetData}>
					<HelideckCoverForm onProceed={setCoverSheetData} />
				</Zippy>

				<Zippy headerTitle="Summary Sheet" isValid={!!summaryData}>
					<HelideckDropSummaryForm onProceed={setSummaryData} />
				</Zippy>

				<Zippy
					headerTitle="Visual Inspection Report"
					isValid={viItems.length > 0}
				>
					<HelideckVIItemsForm onProceed={setVIItems} />
				</Zippy>

				<Zippy
					headerTitle="Drawing Sheet"
					isValid={drawingSheetData.length > 0}
				>
					<HelideckDrawingSheetForm onProceed={setDrawingSheetData} />
				</Zippy>

				<Zippy headerTitle="Inspection Sketch" isValid={sketchData.length > 0}>
					<HelideckDropSketchForm onProceed={setSketchData} />
				</Zippy>

				<Zippy headerTitle="Net Drop Test Result" isValid={dtItems.length > 0}>
					<HelideckDropTestItemsForm onProceed={setDTItems} />
				</Zippy>

				<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
					<ETAnomalySummary
						anomalyData={anomalyData}
						onDataChange={setAnomalyData}
					/>
				</Zippy>

				<Zippy
					headerTitle="Photographic Record"
					isValid={photographicData.length > 0}
				>
					<HelideckDropPhotoForm onProceed={setPhotographicData} />
				</Zippy>

				<div className="d-flex justify-content-end">
					<Elevation
						isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
					>
						<Button
							label="Save and Preview Report"
							disabled={error}
							onClick={handleSaveAndPreview}
						/>
					</Elevation>
				</div>
			</Fragment>
		</Fragment>
	);
};

export default CreateHelideckNetDropReport;
